/**
 * 14개 표준 컨트롤러의 단일 진실 공급원(SSOT) 레지스트리.
 *
 * - injectControllers: sentinel/script 로 멱등 주입
 * - controller-validator: required 셀렉터로 DOM 계약 충족 검증 (warn-only)
 * - anchor-scanner / controller-normalizer: anchorScanKey/conflictingPatterns 로 보강 단계 트리거
 *
 * 새 컨트롤러 추가 시 controllers/{name}.ts 생성 후 이 배열에만 등록하면 된다.
 */
import { SCROLL_LOCK_CONTROLLER_SCRIPT } from './scroll-lock.js';
import { RSVP_CONTROLLER_SCRIPT } from './rsvp.js';
import { GUESTBOOK_CONTROLLER_SCRIPT } from './guestbook.js';
import { GALLERY_CONTROLLER_SCRIPT } from './gallery.js';
import { BGM_CONTROLLER_SCRIPT } from './bgm.js';
import { NAVIGATION_CONTROLLER_SCRIPT } from './navigation.js';
import { COUNTDOWN_CONTROLLER_SCRIPT } from './countdown.js';
import { NAVER_MAP_CONTROLLER_SCRIPT } from './naver-map.js';
import { HERO_TEXT_CONTROLLER_SCRIPT } from './hero-text.js';
import { CALENDAR_CONTROLLER_SCRIPT } from './calendar.js';
import { TIMELINE_CONTROLLER_SCRIPT } from './timeline.js';
import { BANK_ACCORDION_CONTROLLER_SCRIPT } from './bank-accordion.js';
import { COPY_TOAST_CONTROLLER_SCRIPT } from './copy-toast.js';
import { CONTACT_OVERLAY_CONTROLLER_SCRIPT } from './contact-overlay.js';
import { VIDEO_THUMB_CONTROLLER_SCRIPT } from './video-thumb.js';
export const CONTROLLER_CONTRACTS = [
    {
        // 다른 컨트롤러가 호출하는 window.__fwScrollLock 을 먼저 정의하도록 배열 맨 앞.
        key: 'scrollLock',
        sentinel: /data-fw-scroll-lock-controller/,
        script: SCROLL_LOCK_CONTROLLER_SCRIPT,
        optOut: /data-fw-scroll-lock-skip/,
        // DOM 마크업 의존 없음(전역 함수만 정의) — validator 형식 충족용으로 body 지정.
        required: [
            { selector: 'body', role: 'host', required: false },
        ],
    },
    {
        key: 'rsvp',
        sentinel: /data-fw-rsvp-controller/,
        script: RSVP_CONTROLLER_SCRIPT,
        optOut: /data-fw-rsvp-skip/,
        required: [
            { selector: '[data-rsvp-form], #rsvpForm', role: 'form' },
            { selector: '[data-rsvp-overlay], #rsvpOverlay, .rsvp-overlay', role: 'overlay', required: false },
        ],
        anchorScanKey: 'rsvp',
        // 레거시 템플릿이 멀티-기능 IIFE 안에 직접 정의해 둔 RSVP 핸들러 — 표준 컨트롤러와 충돌하므로 import 단계에서 strip
        conflictingPatterns: [
            /function\s+initRsvp\s*\([\s\S]*?\n\s*\}/g,
        ],
    },
    {
        key: 'guestbook',
        sentinel: /data-fw-gb-controller/,
        script: GUESTBOOK_CONTROLLER_SCRIPT,
        optOut: /data-fw-gb-skip/,
        required: [
            { selector: '[data-guestbook-form], #guestbookForm', role: 'form' },
            { selector: '[data-guestbook-list], #guestbookList', role: 'list' },
            { selector: '[data-guestbook-open], [data-guestbook-write], #guestbookOpenBtn, #guestbookWriteBtn', role: 'openBtn', required: false },
        ],
        anchorScanKey: 'guestbook',
        // 레거시 템플릿이 멀티-기능 IIFE 안에 정의한 방명록 핸들러 — 표준 컨트롤러와 충돌하므로 import 단계에서 strip
        conflictingPatterns: [
            /function\s+initGuestbook\s*\([\s\S]*?\n\s*\}/g,
            /function\s+initGuestbookForm\s*\([\s\S]*?\n\s*\}/g,
            /function\s+loadGuestbookEntries\s*\([\s\S]*?\n\s*\}/g,
        ],
    },
    {
        key: 'gallery',
        sentinel: /data-fw-gallery-controller/,
        script: GALLERY_CONTROLLER_SCRIPT,
        // 디자이너가 자체 라이트박스를 구현한 경우의 opt-out 시그널
        optOut: /id=["']lightboxOverlay["']|data-fw-gallery-skip/,
        required: [
            { selector: '[data-fw-gallery], [data-fiket-gallery], .gallery-grid', role: 'gallery' },
        ],
        anchorScanKey: 'gallery',
    },
    {
        key: 'bgm',
        sentinel: /data-fw-bgm-controller/,
        script: BGM_CONTROLLER_SCRIPT,
        optOut: /data-fw-bgm-skip/,
        required: [
            { selector: '[data-fw-bgm-audio], #bgmPlayer', role: 'audio' },
            { selector: '[data-fw-bgm-toggle], #bgmToggle', role: 'toggle', required: false },
        ],
        anchorScanKey: 'bgm',
    },
    {
        key: 'navigation',
        sentinel: /data-fw-nav-controller/,
        script: NAVIGATION_CONTROLLER_SCRIPT,
        optOut: /data-fw-nav-skip/,
        required: [
            { selector: '[data-fw-nav]', role: 'container' },
        ],
        anchorScanKey: 'navigation',
    },
    {
        key: 'countdown',
        sentinel: /data-fw-countdown-controller/,
        script: COUNTDOWN_CONTROLLER_SCRIPT,
        optOut: /data-fw-countdown-skip/,
        required: [
            { selector: '[data-fw-countdown], #ceremonyCountdown', role: 'container' },
        ],
        anchorScanKey: 'countdown',
    },
    {
        key: 'naverMap',
        sentinel: /data-fw-naver-map-controller/,
        script: NAVER_MAP_CONTROLLER_SCRIPT,
        optOut: /data-fw-naver-map-skip/,
        required: [
            { selector: '[data-fw-naver-map], #naverMap', role: 'container' },
        ],
        anchorScanKey: 'naverMap',
    },
    {
        key: 'heroText',
        sentinel: /data-fw-hero-text-controller/,
        script: HERO_TEXT_CONTROLLER_SCRIPT,
        optOut: /data-fw-hero-text-skip/,
        required: [
            { selector: '[data-fw-hero-text]', role: 'target' },
        ],
        anchorScanKey: 'heroText',
    },
    {
        key: 'calendar',
        sentinel: /data-fw-calendar-controller/,
        script: CALENDAR_CONTROLLER_SCRIPT,
        optOut: /data-fw-calendar-skip/,
        required: [
            { selector: '[data-fw-calendar]', role: 'container' },
            { selector: '[data-fw-calendar-body]', role: 'body' },
            { selector: '[data-fw-calendar-month]', role: 'month', required: false },
        ],
        anchorScanKey: 'calendar',
        // 원본 템플릿의 하드코딩 캘린더 IIFE — normalizer 가 strip
        conflictingPatterns: [
            /\(function\s+renderCalendar\s*\(\)[\s\S]*?\}\)\(\);?/g,
            /document\.getElementById\(['"]calBody['"]\)\.innerHTML\s*=/g,
        ],
    },
    {
        key: 'timeline',
        sentinel: /data-fw-timeline-controller/,
        script: TIMELINE_CONTROLLER_SCRIPT,
        optOut: /data-fw-timeline-skip/,
        required: [
            { selector: '[data-fw-timeline]', role: 'container' },
            { selector: '[data-fw-timeline-item]', role: 'item' },
        ],
        anchorScanKey: 'timeline',
    },
    {
        key: 'bankAccordion',
        sentinel: /data-fw-bank-accordion-controller/,
        script: BANK_ACCORDION_CONTROLLER_SCRIPT,
        optOut: /data-fw-bank-accordion-skip/,
        required: [
            { selector: '[data-fw-bank-toggle]', role: 'toggle' },
        ],
        anchorScanKey: 'bankAccordion',
    },
    {
        key: 'copyToast',
        sentinel: /data-fw-copy-toast-controller/,
        script: COPY_TOAST_CONTROLLER_SCRIPT,
        optOut: /data-fw-copy-toast-skip/,
        required: [
            { selector: '[data-fw-copy]', role: 'button' },
        ],
        anchorScanKey: 'copyToast',
    },
    {
        key: 'contactOverlay',
        sentinel: /data-fw-contact-overlay-controller/,
        script: CONTACT_OVERLAY_CONTROLLER_SCRIPT,
        optOut: /data-fw-contact-overlay-skip/,
        required: [
            { selector: '[data-fw-contact-open]', role: 'openBtn' },
            { selector: '[data-fw-contact-overlay]', role: 'overlay' },
        ],
        anchorScanKey: 'contactOverlay',
    },
    {
        key: 'videoThumb',
        sentinel: /data-fw-video-thumb-controller/,
        script: VIDEO_THUMB_CONTROLLER_SCRIPT,
        optOut: /data-fw-video-thumb-skip/,
        required: [
            { selector: '[data-fw-video-thumb]', role: 'thumb' },
            { selector: '[data-fw-video-frame]', role: 'iframe' },
        ],
        anchorScanKey: 'videoThumb',
    },
];
/** key → contract 빠른 lookup. */
export const CONTROLLER_CONTRACT_BY_KEY = CONTROLLER_CONTRACTS.reduce((acc, c) => {
    acc[c.key] = c;
    return acc;
}, {});
//# sourceMappingURL=contracts.js.map